import { withAuthenticationRequired } from "@auth0/auth0-react";
import { store } from "../store";
import { useTranslation } from "react-i18next";
import DevicesTable from "../components/tables/Devices";
import {
  Grid,
  Paper,
  Typography,
  Box,
  alpha,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Devices as DevicesIcon,
  WaterDrop,
  ElectricBolt,
  Tv,
  DevicesOther,
  Business,
  FilterList,
  Inventory2,
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import DeviceCards from "../components/DeviceCards";

function Devices(props) {
  const { t } = useTranslation("common");
  const isMobile = useSelector((state) => state.isMobile);
  const devices = useSelector((state) => state.devices);
  const residences = useSelector((state) => state.residences);
  const establishments = useSelector((state) => state.establishments);
  const packs = useSelector((state) => state.packs);

  const [selectedEstablishment, setSelectedEstablishment] = useState("all");
  const [selectedDeviceType, setSelectedDeviceType] = useState("all");

  // Fonction pour obtenir l'établissement d'un device
  const getDeviceEstablishment = (device) => {
    // Trouver le pack qui contient ce device
    const pack = packs.find((p) =>
      p.devices?.some((d) => d.dev_eui === device.dev_eui)
    );
    if (!pack) return null;

    // Trouver la résidence associée au pack
    const residence = residences.find((r) => r.pack_id === pack.id);
    if (!residence) return null;

    // Trouver l'établissement
    return establishments.find(
      (e) => parseInt(e.id) === parseInt(residence.establishment_id)
    );
  };

  // Fonction pour obtenir le type de device
  const getDeviceType = (device) => {
    const typeName =
      device?.device_brand?.device_type?.name ||
      device?.device_type?.name ||
      "";
    return typeName.toLowerCase();
  };

  // Filtrer les devices selon l'établissement sélectionné
  const filteredDevices =
    selectedEstablishment === "all"
      ? devices
      : selectedEstablishment === "unassigned"
        ? devices.filter((device) => !getDeviceEstablishment(device))
        : devices.filter((device) => {
            const estab = getDeviceEstablishment(device);
            return estab && parseInt(estab.id) === parseInt(selectedEstablishment);
          });

  // Calculer les statistiques par type
  const totalDevices = filteredDevices.length;
  const waterDevices = filteredDevices.filter((d) => {
    const type = getDeviceType(d);
    return type.includes("water") || type.includes("eau");
  }).length;
  const elecDevices = filteredDevices.filter((d) => {
    const type = getDeviceType(d);
    return type.includes("elec") || type.includes("power") || type.includes("énergie");
  }).length;
  const screenDevices = filteredDevices.filter((d) => {
    const type = getDeviceType(d);
    return type.includes("screen") || type.includes("écran") || type.includes("display");
  }).length;
  const otherDevices = totalDevices - waterDevices - elecDevices - screenDevices;

  const statsCards = [
    {
      title: t("common.total"),
      value: totalDevices,
      icon: DevicesIcon,
      color: "#78a882",
      bgColor: alpha("#78a882", 0.08),
      type: "all",
    },
    {
      title: t("common.water_meter") || "Eau",
      value: waterDevices,
      icon: WaterDrop,
      color: "#6b9dad",
      bgColor: alpha("#6b9dad", 0.08),
      type: "water",
    },
    {
      title: t("common.electricity_meter") || "Électricité",
      value: elecDevices,
      icon: ElectricBolt,
      color: "#c4a574",
      bgColor: alpha("#c4a574", 0.08),
      type: "electricity",
    },
    {
      title: t("common.screens") || "Écrans",
      value: screenDevices,
      icon: Tv,
      color: "#8a9ba8",
      bgColor: alpha("#8a9ba8", 0.08),
      type: "screen",
    },
  ];

  // Ajouter la carte "Autres" seulement si il y en a
  if (otherDevices > 0) {
    statsCards.push({
      title: t("common.other") || "Autres",
      value: otherDevices,
      icon: DevicesOther,
      color: "#7a8a7a",
      bgColor: alpha("#7a8a7a", 0.08),
      type: "other",
    });
  }

  // Fonction pour filtrer par type de device
  const filterByDeviceType = (devicesList, type) => {
    if (type === "all") return devicesList;

    return devicesList.filter((d) => {
      const deviceType = getDeviceType(d);
      switch (type) {
        case "water":
          return deviceType.includes("water") || deviceType.includes("eau");
        case "electricity":
          return deviceType.includes("elec") || deviceType.includes("power") || deviceType.includes("énergie");
        case "screen":
          return deviceType.includes("screen") || deviceType.includes("écran") || deviceType.includes("display");
        case "other":
          return !(
            deviceType.includes("water") || deviceType.includes("eau") ||
            deviceType.includes("elec") || deviceType.includes("power") || deviceType.includes("énergie") ||
            deviceType.includes("screen") || deviceType.includes("écran") || deviceType.includes("display")
          );
        default:
          return true;
      }
    });
  };

  // Appliquer le filtre par type sur les devices déjà filtrés par établissement
  const displayedDevices = filterByDeviceType(filteredDevices, selectedDeviceType);

  useEffect(() => {
    store.dispatch({ type: "pageTitle/update", payload: t("common.devices") });

    let pageActions = [
      {
        name: t("common.add_new", { name: t("common.device") }),
        icon: "Add",
        url: "/add/device",
      },
    ];

    store.dispatch({ type: "pageActions/update", payload: pageActions });
  }, [t]);

  return (
    <Grid container spacing={3}>
      {/* Cartes statistiques */}
      <Grid item xs={12}>
        <Grid container spacing={2}>
          {statsCards.map((stat, index) => (
            <Grid item xs={6} sm={statsCards.length === 5 ? 2.4 : 3} key={index}>
              <Paper
                elevation={0}
                onClick={() => setSelectedDeviceType(selectedDeviceType === stat.type ? "all" : stat.type)}
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  background: stat.bgColor,
                  border: selectedDeviceType === stat.type
                    ? `2px solid ${stat.color}`
                    : `1px solid ${alpha(stat.color, 0.2)}`,
                  transition: "all 0.2s ease-in-out",
                  cursor: "pointer",
                  transform: selectedDeviceType === stat.type ? "translateY(-2px)" : "none",
                  boxShadow: selectedDeviceType === stat.type
                    ? `0 6px 24px ${alpha(stat.color, 0.35)}`
                    : "none",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 4px 20px ${alpha(stat.color, 0.2)}`,
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor: alpha(stat.color, 0.15),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <stat.icon sx={{ color: stat.color, fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 700, color: stat.color, lineHeight: 1 }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", mt: 0.5 }}
                    >
                      {stat.title}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Grid>

      {/* Filtre par établissement */}
      <Grid item xs={12}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 3,
            backgroundColor: alpha("#7a8a7a", 0.05),
            border: `1px solid ${alpha("#7a8a7a", 0.1)}`,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FilterList sx={{ color: "#7a8a7a", fontSize: 20 }} />
            <Typography variant="body2" sx={{ color: "#9aa89a", fontWeight: 500 }}>
              {t("common.filter_table")}
            </Typography>
          </Box>
          <FormControl size="small" sx={{ minWidth: 250 }}>
            <InputLabel id="establishment-filter-label">
              {t("common.establishment")}
            </InputLabel>
            <Select
              labelId="establishment-filter-label"
              value={selectedEstablishment}
              label={t("common.establishment")}
              onChange={(e) => setSelectedEstablishment(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: alpha("#7a8a7a", 0.3),
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: alpha("#7a8a7a", 0.5),
                },
              }}
            >
              <MenuItem value="all">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Inventory2 sx={{ fontSize: 18, color: "#78a882" }} />
                  {t("common.all")} ({devices.length})
                </Box>
              </MenuItem>
              <MenuItem value="unassigned">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <DevicesOther sx={{ fontSize: 18, color: "#c4a574" }} />
                  {t("common.not_assigned")} (
                  {devices.filter((d) => !getDeviceEstablishment(d)).length})
                </Box>
              </MenuItem>
              {establishments.map((estab) => {
                const count = devices.filter((d) => {
                  const e = getDeviceEstablishment(d);
                  return e && parseInt(e.id) === parseInt(estab.id);
                }).length;
                return (
                  <MenuItem key={estab.id} value={estab.id}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Business sx={{ fontSize: 18, color: "#9a8878" }} />
                      {estab.name} ({count})
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          {(selectedEstablishment !== "all" || selectedDeviceType !== "all") && (
            <Typography
              variant="caption"
              sx={{
                color: "#9aa89a",
                ml: "auto",
              }}
            >
              {displayedDevices.length} {t("common.devices").toLowerCase()}
              {selectedDeviceType !== "all" && (
                <span
                  onClick={() => setSelectedDeviceType("all")}
                  style={{ marginLeft: 8, cursor: "pointer", textDecoration: "underline" }}
                >
                  ✕ Réinitialiser
                </span>
              )}
            </Typography>
          )}
        </Paper>
      </Grid>

      {/* Tableau des devices */}
      <Grid item xs={12}>
        {isMobile ? <DeviceCards /> : <DevicesTable filteredDevices={displayedDevices} />}
      </Grid>
    </Grid>
  );
}

export default withAuthenticationRequired(Devices);
